import { localStorageService } from 'client/service/storage';
import { appendQueryStringToUrl } from 'helpers/utils/url';
import PageTypes from 'constants/pageTypes';
import { pages } from 'constants/urls';
import { logger } from 'logging';
import {
  SearchSuggestion,
  ManualQuery,
  PopularSuggestion,
} from 'types/searchSuggestion';
import { ListingProduct } from 'store/categoryListing/types';
import {
  searchTypes,
  searchTypeRaw,
  TEMPLATE_FOR_BRAND_SEARCH,
  SEARCH_ROOT,
  RECENT_SEARCH_LIST,
  AD_TAG,
  STORE_SUGGESTION_CLICKED,
} from 'constants/searchSuggestion';
import { pushEvent } from 'helpers/utils/dataLayer';
import { SiteNavigation, siteSubNavigationPrefix } from 'constants/dataLayer';

const ellipses = '...';

export function handleSearchSuggestionClick(
  suggestion: SearchSuggestion,
  page: string,
  searchField?: string,
  position?: number
): void {
  const { q, type, url, listItemType, storeType, mwebUrl } = suggestion;

  let storeSuggestionUrl = mwebUrl || url;
  let baseUrl = url;
  let searchKeyword = '';

  if (typeof window !== 'undefined' && window?.datalayer) {
    searchKeyword = window.datalayer?.searchKeyword;
  }
  const customUrlParams = new URLSearchParams();

  const isSpecialSuggestion = type === searchTypes.SPECIAL_SUGGESTION;
  let searchTypeV2 = '';
  let searchType = searchTypes.MISCELLANEOUS;
  if (type === searchTypeRaw.PRODUCT) {
    searchType = searchTypes.POPULAR_PRODUCTS;
  } else if (type === searchTypes.TRENDING) {
    searchType = searchTypes.TRENDING;
  } else if (isSpecialSuggestion) {
    searchType = searchTypes.SEARCH_FROM_STORE;
    searchTypeV2 = storeType || '';
  }

  if (listItemType === searchTypes.TRENDING) {
    searchType = searchTypes.TRENDING;
  }

  if (listItemType === searchTypes.HISTORY) {
    searchType = searchTypes.HISTORY;
  }
  if (isSpecialSuggestion && searchTypeV2) {
    customUrlParams.append('searchTypeV2', searchTypeV2);
  }
  let rootParam = SEARCH_ROOT;
  if (type === searchTypeRaw.BRAND) {
    rootParam = `${rootParam}${TEMPLATE_FOR_BRAND_SEARCH}${q}`;
    /**
     * * This check is essential as we have observed some brand urls
     * * we do not get "/" at the start which is critical for navigation
     */
    if (baseUrl.indexOf('/') !== 0) {
      baseUrl = `/${baseUrl}`;
    }
  }
  // TODO: Handle cases for trending searches and history;
  customUrlParams.append('root', rootParam);
  if (baseUrl.indexOf('searchType') > -1) {
    baseUrl = baseUrl.replace(/searchType.*&/, '');
    baseUrl = baseUrl.replace(/searchType.*$/, '');
  }
  customUrlParams.append('searchType', searchType);
  customUrlParams.append('suggestionType', type);
  position && customUrlParams.append('ssp', position.toString());
  searchField && customUrlParams.append('tst', searchField);
  customUrlParams.append('searchItem', q);
  customUrlParams.append('sourcepage', page || PageTypes.SEARCH_SUGGESTION);
  customUrlParams.append('searchKeyword', searchKeyword);
  // search redirection query param need to be added if url is of pages other then search page

  if (!(baseUrl?.indexOf(pages.SEARCH_RESULTS) > -1)) {
    customUrlParams.append('searchRedirect', '1');
  }
  let paramString = '';
  customUrlParams.forEach((value, key) => {
    paramString += `${key}=${value}&`;
  });

  const redirectionUrl = appendQueryStringToUrl(baseUrl, paramString);
  if (isSpecialSuggestion) {
    const storeSuggestionData = {
      searchType: searchTypes.SPECIAL_SUGGESTION,
      searchTypeV2: storeType,
      pageType: 'searchPage',
      siteNavigation: SiteNavigation.SEARCH,
      listingOpened: `search:query:${q}`,
      searchKeyword: q,
      page: `search:${q}`,
      searchLocation: page || PageTypes.SEARCH_SUGGESTION,
      searchTypedKeyword: searchField || '',
      siteSubNavigation: `${siteSubNavigationPrefix.SEARCH}:${q}`,
      listingType: 'searchListingPage',
      listingValue: q,
    };
    pushEvent(STORE_SUGGESTION_CLICKED, { storeSuggestionData });

    window.location.href = appendQueryStringToUrl(
      storeSuggestionUrl,
      paramString
    );
    return;
  }
  window.history.replaceState(null, '', redirectionUrl);
  window.location.href = redirectionUrl;
}

export const handlePopularSuggestionClick = (
  suggestion: PopularSuggestion,
  page: string,
  position: number
) => {
  const { q, type, url } = suggestion;
  const customUrlParams = new URLSearchParams();
  customUrlParams.append('searchWidget', type);
  position && customUrlParams.append('ssp', position.toString());
  customUrlParams.append('q', q);
  customUrlParams.append('sourcepage', page || PageTypes.SEARCH_SUGGESTION);
  let paramString = '';
  customUrlParams.forEach((value, key) => {
    paramString += `${key}=${value}&`;
  });

  const redirectionUrl = appendQueryStringToUrl(url, paramString);
  window.history.replaceState(null, '', redirectionUrl);
  window.location.href = redirectionUrl;
};

interface HighlightedSearchField {
  partBefore?: string;
  partMatching?: string;
  partAfter?: string;
  match: boolean;
}

export const getHighlightedSearchField = (
  searchField: string,
  q: string
): HighlightedSearchField => {
  const searchString = searchField.toLowerCase();
  const productName = q;
  const searchStringLength = searchString.length;
  const searchStringIndex = productName.toLowerCase().indexOf(searchString);

  if (searchStringIndex < 0) {
    return {
      match: false,
    };
  }
  const partBefore = productName.substr(0, searchStringIndex);
  const partMatching = productName.substr(
    searchStringIndex,
    searchStringLength
  );
  const partAfter = productName.substr(searchStringIndex + searchStringLength);

  return {
    partBefore,
    partMatching,
    partAfter,
    match: true,
  };
};

// multiline Support: Add ellpises at the last of the text
export const addEllipses = (
  queryChar: string,
  maxChar: number,
  minChar: number
) => {
  if (queryChar.length > maxChar) {
    return (queryChar = queryChar.substring(0, minChar) + ellipses);
  } else {
    return queryChar;
  }
};

// Find first product without AD tag
export const findFirstNonAdProduct = (
  products: ListingProduct[]
): ListingProduct | undefined =>
  products?.find((product) => {
    const tags = product?.dynamicTags;
    if (!tags) {
      return true; // Accept if no tags
    }
    // Skip products with AD tag (handle both string[] and TagObject[])
    return !tags.some((tag) =>
      typeof tag === 'string' ? tag === AD_TAG : tag?.name === AD_TAG
    );
  });

export const handleManualSearchForHistory = (
  products: ListingProduct[],
  activeFilters: any
) => {
  const item = findFirstNonAdProduct(products);

  if (!item) return; // No valid product found

  const searchHistory = getSearchHistory();
  if (searchHistory?.length) {
    const searchItems = searchHistory.map((searchItem: SearchSuggestion) => {
      const rawQuery = activeFilters?.q
        ? activeFilters.q?.replace(/\+/g, '%20')
        : activeFilters?.searchTerm || activeFilters?.searchItem || '';
      const hasExtraPercent = /%(?![0-9A-Fa-f]{2})/.test(rawQuery);
      const cleanedQuery = hasExtraPercent
        ? rawQuery.replace(/%(?![0-9A-Fa-f]{2})/g, 'percent')
        : rawQuery;
      const decodedQuery = decodeURIComponent(cleanedQuery);
      const searchQuery = decodedQuery;
      if (
        searchItem?.q?.toLowerCase() == searchQuery?.toLowerCase() &&
        item?.imageUrl &&
        !searchItem.image // Only update if no image exists
      ) {
        searchItem.image = item.imageUrl;
      }
      return searchItem;
    });
    localStorage.setItem(RECENT_SEARCH_LIST, JSON.stringify(searchItems));
  }
};

export const deleteRecentSearch = () => {
  localStorage.removeItem(RECENT_SEARCH_LIST);
};

export function handleSearchSuggestionForHistory(
  suggestion: SearchSuggestion | ManualQuery,
  maxItems: number = 10
) {
  if (suggestion.type === searchTypes.SPECIAL_SUGGESTION) {
    return;
  }
  const suggestionItem: SearchSuggestion | ManualQuery = {
    ...suggestion,
    listItemType: searchTypes.RECENT_SEARCH_LIST_ITEM,
  };
  try {
    const searchHistory = localStorageService.getItem(RECENT_SEARCH_LIST);
    let searchItems = [];

    if (searchHistory) {
      try {
        searchItems = JSON.parse(searchHistory);
      } catch (err) {
        logger.error(err, 'Parsing "search history"');
      }
    }

    const filteredList = searchItems.filter((searchItem: SearchSuggestion) => {
      const { q: queryFromHistory } = searchItem;
      const { q: newQuery } = suggestionItem;
      if (queryFromHistory && newQuery) {
        if (queryFromHistory.toLowerCase() === newQuery.toLocaleLowerCase()) {
          return false;
        }
      }

      return true;
    });

    searchItems = [suggestionItem, ...filteredList];
    if (searchItems.length > maxItems) {
      searchItems.pop();
    }
    localStorageService.setItem(
      RECENT_SEARCH_LIST,
      JSON.stringify(searchItems)
    );
  } catch (error) {
    logger.error(error, 'CONTEXT: handleSearchSuggestionForHistory');
  }
}

export const getSearchHistory = (): SearchSuggestion[] => {
  if (__SERVER__) {
    return [];
  }

  try {
    const searchHistory = localStorageService.getItem(RECENT_SEARCH_LIST);
    if (searchHistory) {
      const searchHistoryItems: SearchSuggestion[] = JSON.parse(searchHistory);
      return searchHistoryItems;
    }
  } catch (err) {
    logger.error(err, 'getSearchHistory');
  }

  return [];
};

export const handleNoResultKeywordsForHistory = (keyword: string): void => {
  const searchHistory = getSearchHistory();
  if (searchHistory?.length > 0) {
    const refinedHistory = searchHistory.filter(
      (item: SearchSuggestion) =>
        item.q?.toLowerCase() !== keyword?.toLowerCase()
    );
    localStorage.setItem(RECENT_SEARCH_LIST, JSON.stringify(refinedHistory));
  }
};

interface ManualSearchData {
  page: string;
  searchQuery: string;
}

export const handleManualSearch = (
  { page, searchQuery }: ManualSearchData,
  historyMaxItems: number,
  isVoiceSearch: boolean = false
) => {
  const encodedSearchQuery = encodeURIComponent(searchQuery);
  let searchUrl = appendQueryStringToUrl(
    pages.SEARCH_RESULTS,
    `q=${encodedSearchQuery}`
  );

  const queryParamsForSearch = new URLSearchParams();
  queryParamsForSearch.append('root', SEARCH_ROOT);
  const searchType = isVoiceSearch ? searchTypes.VOICE : searchTypes.MANUAL;
  queryParamsForSearch.append('searchType', searchType);
  queryParamsForSearch.append(
    'sourcepage',
    page || PageTypes.SEARCH_SUGGESTION
  );

  const queryString = queryParamsForSearch.toString();
  searchUrl = appendQueryStringToUrl(searchUrl, queryString);

  /**
   * ? Why handleSearchSuggestionForHistory in here you may ask?
   * * This is because manual search item also needs the URL
   * * which is created inside this method...handleManualSearch
   */
  handleSearchSuggestionForHistory(
    {
      q: searchQuery,
      type: searchTypes.QUERY,
      url: searchUrl,
    },
    historyMaxItems
  );

  window.history.replaceState(null, '', searchUrl);

  window.location.href = searchUrl;
};
